import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';

interface PersonaBadgeProps {
  personaId: string;
  personaName: string;
  size: 'xs' | 'sm' | 'md' | 'lg';
  revision?: number;
  health?: 'green' | 'amber' | 'red';
  showTooltip?: boolean;
  clickable?: boolean;
  className?: string;
  // Suppress the inline name render at md/lg sizes — use when the
  // surrounding header already shows a styled name and the badge
  // would just duplicate it. Tooltip behavior gets enabled as a
  // fallback so the name is still reachable on hover.
  hideName?: boolean;
}

// Deterministic color from name — hash to palette index
const PALETTE = [
  '#7c3aed', '#2563eb', '#0891b2', '#059669',
  '#d97706', '#dc2626', '#db2777', '#9333ea',
  '#4f46e5', '#0d9488', '#ca8a04', '#6366f1',
];

function nameToColor(name: string): string {
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  return PALETTE[Math.abs(hash) % PALETTE.length];
}

function getInitials(name: string): string {
  return name
    .split(/[_\s-]+/)
    .filter(Boolean)
    .slice(0, 2)
    .map(w => w[0])
    .join('')
    .toUpperCase();
}

const sizeConfig = {
  xs: { circle: 20, fontSize: 8, showName: false },
  sm: { circle: 24, fontSize: 9, showName: false },
  md: { circle: 32, fontSize: 11, showName: true },
  lg: { circle: 40, fontSize: 14, showName: true },
};

const PersonaBadge: React.FC<PersonaBadgeProps> = ({
  personaId,
  personaName,
  size,
  revision,
  health,
  showTooltip = true,
  clickable = true,
  className = '',
  hideName = false,
}) => {
  const navigate = useNavigate();
  const [hovered, setHovered] = useState(false);

  const color = nameToColor(personaName);
  const initials = getInitials(personaName);
  const cfg = sizeConfig[size];

  const healthRing = health ? {
    green: 'ring-green-500',
    amber: 'ring-amber-500',
    red: 'ring-red-500',
  }[health] : '';

  return (
    <span
      className={`relative inline-flex items-center gap-1.5 ${clickable ? 'cursor-pointer' : ''} ${className}`}
      onClick={clickable ? () => navigate(`/dashboards/personas/${personaId}`) : undefined}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <span
        className={`inline-flex items-center justify-center rounded-full text-white font-bold shrink-0 transition-all duration-200 ${health ? `ring-2 ${healthRing}` : ''}`}
        style={{
          width: cfg.circle,
          height: cfg.circle,
          fontSize: cfg.fontSize,
          backgroundColor: color,
          boxShadow: hovered ? `0 0 12px ${color}40` : 'none',
        }}
      >
        {initials}
      </span>
      {(!hideName && (cfg.showName || (size === 'sm' && hovered))) && (
        <span className="text-sm truncate max-w-[160px]">
          {personaName.replace(/_/g, ' ')}
        </span>
      )}
      {!hideName && size === 'lg' && revision != null && (
        <span className="text-xs opacity-60 ml-1">rev {revision}</span>
      )}
      {showTooltip && hovered && (hideName || !cfg.showName) && (
        <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1 px-2 py-1 text-xs bg-gray-900 text-white rounded whitespace-nowrap z-50 pointer-events-none">
          {personaName.replace(/_/g, ' ')}{revision != null ? ` (rev ${revision})` : ''}
        </span>
      )}
    </span>
  );
};

export default PersonaBadge;
